import {
    Component,
    input,
    output,
    OnInit,
    signal,
    ChangeDetectorRef,
    ChangeDetectionStrategy,
    ElementRef,
    effect,
    model,
} from "@angular/core";

import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { Workflow as WorkflowTypes } from "./service/Workflow";
import { S25EditableTextComponent } from "../s25-editable/s25-editable-text/s25.editable.text.component";
import { ExpandTrigger, WFError, WorkflowSteps } from "./workflow.process.types";
import { S25CheckboxModule } from "../s25-checkbox/s25.checkbox.module";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { S25InfoMessageModule } from "../s25-info-message/s25.info.message.module";
import { S25Util } from "../../util/s25-util";
import { AlertService } from "../../services/alert.service";
import { WorkflowUtil } from "./workflow.util";

@TypeManagerDecorator("workflow-stages")
@Component({
    selector: "workflow-stages",
    template: `
        <div class="stages-container">
            @if (error()?.message) {
                <p class="ngRed">{{ error()!.message }}</p>
            }
            <div class="stages-header">
                <h3>Stages</h3>
                <button class="aw-button aw-button--primary" (click)="addStage()">Add Stage</button>
            </div>
            <div class="stages-list">
                @for (stage of workflowProcess().stages; track stage.workflowStageId || stage.id) {
                    <div
                        class="stage-item"
                        [class.error-index]="errorAtIndex($index)"
                        [attr.data-stage-id]="stage.workflowStageId || stage.id"
                    >
                        <div class="workflow-header">
                            <div class="workflow-info">
                                <s25-ng-editable-text
                                    [val]="stage.stageName"
                                    [hasCommit]="true"
                                    [hasCommitButton]="true"
                                    [hasCancelButton]="true"
                                    [customValidation]="getStageNameValidation(stage)"
                                    [onCommit]="getStageNameCommitHandler(stage)"
                                    placeholder="Stage name"
                                    class="stage-name"
                                ></s25-ng-editable-text>
                                <div class="stage-flags">
                                    <s25-ng-checkbox
                                        [value]="stage.isStartStage"
                                        [disabled]="stage.isAccumulator! || stage.isApAccumulator!"
                                        (valueChange)="updateStageStartStage(stage, $event)"
                                        >Start</s25-ng-checkbox
                                    >
                                    <s25-ng-checkbox
                                        [value]="stage.isTerminal"
                                        [disabled]="stage.isAccumulator! || stage.isApAccumulator!"
                                        (valueChange)="updateStageTerminal(stage, $event)"
                                        >Terminal</s25-ng-checkbox
                                    >
                                    <s25-ng-checkbox
                                        [value]="stage.isAccumulator"
                                        [disabled]="stage.isApAccumulator!"
                                        (valueChange)="updateStageAccumulator(stage, $event)"
                                        >Accumulator</s25-ng-checkbox
                                    >
                                    <s25-ng-checkbox
                                        [value]="stage.isApAccumulator"
                                        [disabled]="stage.isAccumulator!"
                                        (valueChange)="updateStageApAccumulator(stage, $event)"
                                        >AP Accumulator</s25-ng-checkbox
                                    >
                                    <s25-ng-checkbox
                                        [value]="stage.hasObjectApprovals"
                                        [disabled]="stage.isAccumulator! || stage.isApAccumulator!"
                                        (valueChange)="updateStageObjectApprovals(stage, $event)"
                                    >
                                        Object Approvals
                                        <s25-ng-info-message [isPopover]="true">
                                            When enabled, approval states are automatically generated for each location
                                            and resource in the form's occurrences. Approvers are determined by 25Live
                                            security group permissions. This is only relevant for forms that use object
                                            occurrences.
                                        </s25-ng-info-message>
                                    </s25-ng-checkbox>
                                    <s25-ng-checkbox
                                        [value]="stage.defaultEnabled"
                                        (valueChange)="updateStageDefaultEnabled(stage, $event)"
                                    >
                                        Enabled
                                        <s25-ng-info-message [isPopover]="true"
                                            >This stage can be manually enabled or disabled here, but stage rules may
                                            take precedence based on specific conditions. To review or modify these
                                            rules, see <b>3. Stage Rules</b> below.
                                        </s25-ng-info-message>
                                    </s25-ng-checkbox>
                                </div>
                            </div>
                            <div class="stage-actions">
                                <button
                                    class="btn btn-sm"
                                    (click)="moveStageUp(stage)"
                                    [disabled]="isFirstStage(stage)"
                                    [attr.title]="'Move up'"
                                >
                                    ↑
                                </button>
                                <button
                                    class="btn btn-sm"
                                    (click)="moveStageDown(stage)"
                                    [disabled]="isLastStage(stage)"
                                    [attr.title]="'Move down'"
                                >
                                    ↓
                                </button>
                                <button
                                    class="btn btn-sm"
                                    (click)="toggleStageCollapse(stage)"
                                    [style.padding-inline]="'11px'"
                                >
                                    {{ isStageCollapsed(stage) ? "▼" : "▲" }}
                                </button>
                                <button
                                    class="btn btn-sm btn-danger"
                                    (click)="deleteStage(stage, $event)"
                                    aria-label="Remove stage"
                                >
                                    <s25-ng-icon [type]="'close'" [ariaLabel]="false" />
                                </button>
                            </div>
                        </div>

                        @if (!isStageCollapsed(stage)) {
                            <div class="stage-details">
                                <div class="stage-description">
                                    <textarea
                                        [(ngModel)]="stage.description"
                                        (ngModelChange)="onStageChange()"
                                        placeholder="Description (optional)"
                                        rows="2"
                                    ></textarea>
                                </div>
                            </div>
                        }
                    </div>
                }
            </div>
        </div>
    `,
    styles: `
        .stages-container {
            padding: 20px;
        }

        .stages-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
        }

        .stages-header h3 {
            margin: 0;
            color: var(--color--text-strong);
        }

        .stages-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .stage-item {
            background: var(--color--surface-bg);
            border: 1px solid var(--color--border);
            border-radius: 8px;
            overflow: hidden;
        }

        .stage-item.error-index {
            border-color: var(--color--danger);
        }

        .stage-name {
            font-weight: 500;
            color: var(--color--text-strong);
        }

        .stage-flags {
            display: flex;
            gap: 16px;
        }

        .stage-actions {
            display: flex;
            gap: 8px;
        }

        .stage-details {
            padding: 20px;
            border-top: 1px solid var(--color--border);
        }

        .stage-description {
            margin-bottom: 20px;
        }

        .stage-description textarea {
            width: 100%;
            padding: 8px 12px;
            border: 1px solid var(--color--border-bold);
            border-radius: 4px;
            font-size: 14px;
            resize: vertical;
        }

        .section h4 {
            margin: 0 0 12px 0;
            color: var(--color--text-strong);
            font-size: 14px;
            font-weight: 500;
        }

        s25-ng-info-message {
            position: relative;
            bottom: 1px;
            right: 2px;
        }

        ::ng-deep s25-ng-checkbox span {
            white-space: nowrap;
        }

        .workflow-header {
            padding: 16px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            background: var(--color--surface-2-bg);
        }

        .workflow-header:has(s25-ng-editable-text.editing) {
            align-items: flex-start;
        }

        .workflow-info {
            display: flex;
            flex-direction: column;
            font-size: 0.9rem;
        }

        .workflow-info:has(s25-ng-editable-text.editing) {
            flex: 1;
            gap: 5px;
        }

        .workflow-info s25-ng-editable-text.editing input {
            width: 95%;
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, S25EditableTextComponent, S25CheckboxModule, S25IconComponent, S25InfoMessageModule],
})
export class WorkflowStagesComponent implements OnInit {
    workflowProcess = input.required<WorkflowTypes.WorkflowProcess>();
    error = input<WFError>();
    selectedStageTrigger = model<ExpandTrigger>();
    workflowProcessChange = output<WorkflowTypes.WorkflowProcess>();

    private collapsedStages = signal<Set<number | string>>(new Set());
    protected errorAtIndex = (index: number) => this.error()?.indices?.some((i) => i === index);

    constructor(
        private cd: ChangeDetectorRef,
        private el: ElementRef,
    ) {
        effect(() => {
            const expandTrigger = this.selectedStageTrigger();
            if (expandTrigger && expandTrigger.step === WorkflowSteps.Stages && S25Util.isDefined(expandTrigger.id)) {
                this.expandStageById(expandTrigger.id);
                this.selectedStageTrigger.set(undefined);
            }
        });
    }

    ngOnInit() {
        this.initializeStages();
    }

    private initializeStages() {
        const stages = this.workflowProcess().stages || [];
        this.workflowProcess().stages = stages;

        // Sort stages by sortOrder
        stages.sort((a, b) => {
            const aOrder = a.sortOrder ?? 0;
            const bOrder = b.sortOrder ?? 0;
            return aOrder - bOrder;
        });

        // Reset sortOrder to be 1...N to handle errant cases where default 1 is applied to all stages
        stages.forEach((stage, index) => {
            stage.sortOrder = index + 1;
        });

        // Initialize stage arrays if needed
        for (const stage of stages) {
            if (!stage.actions) {
                stage.actions = [];
            }
            if (!stage.states) {
                stage.states = [];
            }
        }
    }

    addStage() {
        const newStage: WorkflowTypes.WorkflowStage = {
            id: this.getNextNegativeId(),
            workflowProcessId: this.workflowProcess().workflowProcessId,
            stageName: `Stage ${this.workflowProcess().stages!.length + 1}`,
            isTerminal: false,
            isStartStage: false,
            isAccumulator: false,
            isApAccumulator: false,
            hasObjectApprovals: false,
            description: "",
            defaultEnabled: true,
            sortOrder: this.workflowProcess().stages!.length + 1,
            states: [],
            actions: [],
        };

        this.workflowProcess().stages!.push(newStage);
        this.onStageChange();
    }

    private getNextNegativeId(): number {
        const stages = this.workflowProcess().stages || [];
        return stages.reduce((minId, stage) => Math.min(minId, stage.id || -1), -1) - 1;
    }

    deleteStage(stage: WorkflowTypes.WorkflowStage, event: Event) {
        event.stopPropagation();

        const index = this.workflowProcess().stages!.findIndex(
            (s) => (s.workflowStageId && s.workflowStageId === stage.workflowStageId) || (s.id && s.id === stage.id),
        );

        if (index > -1) {
            this.workflowProcess().stages!.splice(index, 1);
            this.onStageChange();
        }
    }

    toggleStageCollapse(stage: WorkflowTypes.WorkflowStage) {
        const stageKey = stage.workflowStageId || stage.id!;
        const collapsed = this.collapsedStages();

        if (collapsed.has(stageKey)) {
            collapsed.delete(stageKey);
        } else {
            collapsed.add(stageKey);
        }

        this.collapsedStages.set(new Set(collapsed));
    }

    isStageCollapsed(stage: WorkflowTypes.WorkflowStage): boolean {
        const stageKey = stage.workflowStageId || stage.id!;
        return this.collapsedStages().has(stageKey);
    }

    updateStageTerminal(stage: WorkflowTypes.WorkflowStage, isTerminal: boolean) {
        if (isTerminal && (stage.isAccumulator || stage.isApAccumulator)) {
            stage.isTerminal = false;
            this.cd.markForCheck();
            return;
        }
        stage.isTerminal = isTerminal;
        this.onStageChange();
    }

    updateStageDefaultEnabled(stage: WorkflowTypes.WorkflowStage, isDefaultEnabled: boolean) {
        stage.defaultEnabled = isDefaultEnabled;
        this.onStageChange();
    }

    updateStageStartStage(stage: WorkflowTypes.WorkflowStage, isStartStage: boolean) {
        // Accumulator stages cannot be start stages
        if (isStartStage && (stage.isAccumulator || stage.isApAccumulator)) {
            stage.isStartStage = false;
            this.cd.markForCheck();
            return;
        }

        // If setting this stage as start stage, unset all other stages
        if (isStartStage) {
            this.workflowProcess().stages!.forEach((s) => {
                if (s !== stage) {
                    s.isStartStage = false;
                }
            });
        }

        stage.isStartStage = isStartStage;
        this.onStageChange();
    }

    updateStageAccumulator(stage: WorkflowTypes.WorkflowStage, isAccumulator: boolean) {
        // Accumulators are synchronization gates - clear incompatible properties when enabling
        if (isAccumulator) {
            stage.isStartStage = false; // Cannot be start (no children exist yet)
            stage.isTerminal = false; // Cannot be terminal (add a stage after instead)
            stage.actions = []; // Cannot have actions (add a stage after instead)
            stage.states = []; // Cannot have states (would require merging two completion mechanisms)
            stage.hasObjectApprovals = false; // Cannot have object approvals (same reason as states)
            stage.isApAccumulator = false;
        }

        stage.isAccumulator = isAccumulator;
        this.onStageChange();
    }

    updateStageApAccumulator(stage: WorkflowTypes.WorkflowStage, isApAccumulator: boolean) {
        if (isApAccumulator) {
            stage.isStartStage = false;
            stage.isTerminal = false;
            stage.actions = [];
            stage.states = [];
            stage.hasObjectApprovals = false;
            stage.isAccumulator = false;
        }

        stage.isApAccumulator = isApAccumulator;
        this.onStageChange();
    }

    updateStageObjectApprovals(stage: WorkflowTypes.WorkflowStage, hasObjectApprovals: boolean) {
        if (hasObjectApprovals && (stage.isAccumulator || stage.isApAccumulator)) {
            stage.hasObjectApprovals = false;
            this.cd.markForCheck();
            return;
        }
        if (hasObjectApprovals && WorkflowUtil.stageActionUsesAssignmentPolicy(stage)) {
            void AlertService.alert(
                "This stage has an action with Use Assignment Policy enabled. Disable that on the action before enabling Object Approvals.",
            );
            stage.hasObjectApprovals = false;
            this.cd.markForCheck();
            return;
        }
        stage.hasObjectApprovals = hasObjectApprovals;
        this.onStageChange();
    }

    onStageChange() {
        this.workflowProcessChange.emit(this.workflowProcess());
        this.cd.detectChanges();
    }

    getStageNameValidation(stage: WorkflowTypes.WorkflowStage) {
        return (value: string) => {
            if (!value || value.trim().length === 0) {
                return ["Stage name is required"];
            }

            // Check for duplicate stage names
            const duplicateStage = this.workflowProcess().stages!.find(
                (s) =>
                    s.stageName === value.trim() &&
                    ((s.workflowStageId && s.workflowStageId !== stage.workflowStageId) || (s.id && s.id !== stage.id)),
            );

            if (duplicateStage) {
                return ["Stage name must be unique within the workflow"];
            }

            return true;
        };
    }

    getStageNameCommitHandler(stage: WorkflowTypes.WorkflowStage) {
        return (value: string) => {
            stage.stageName = value;
            this.onStageChange();
        };
    }

    private getStageIndex(stage: WorkflowTypes.WorkflowStage): number {
        return this.workflowProcess().stages!.findIndex(
            (s) => (s.workflowStageId && s.workflowStageId === stage.workflowStageId) || (s.id && s.id === stage.id),
        );
    }

    isFirstStage(stage: WorkflowTypes.WorkflowStage): boolean {
        return this.getStageIndex(stage) === 0;
    }

    isLastStage(stage: WorkflowTypes.WorkflowStage): boolean {
        const stages = this.workflowProcess().stages!;
        return this.getStageIndex(stage) === stages.length - 1;
    }

    moveStageUp(stage: WorkflowTypes.WorkflowStage) {
        const index = this.getStageIndex(stage);
        if (index > 0) {
            const stages = this.workflowProcess().stages!;
            const previousStage = stages[index - 1];
            // Swap sortOrder values
            const tempOrder = stage.sortOrder;
            stage.sortOrder = previousStage.sortOrder;
            previousStage.sortOrder = tempOrder;
            // Swap positions in array
            stages[index] = previousStage;
            stages[index - 1] = stage;
            this.onStageChange();
        }
    }

    moveStageDown(stage: WorkflowTypes.WorkflowStage) {
        const index = this.getStageIndex(stage);
        const stages = this.workflowProcess().stages!;
        if (index < stages.length - 1) {
            const nextStage = stages[index + 1];
            // Swap sortOrder values
            const tempOrder = stage.sortOrder;
            stage.sortOrder = nextStage.sortOrder;
            nextStage.sortOrder = tempOrder;
            // Swap positions in array
            stages[index] = nextStage;
            stages[index + 1] = stage;
            this.onStageChange();
        }
    }

    expandStageById(stageId: number) {
        const stages = this.workflowProcess().stages;
        const stage = stages?.find((s) => s.workflowStageId === stageId || s.id === stageId);
        if (stage) {
            const stageKey = stage.workflowStageId || stage.id!;
            const collapsed = this.collapsedStages();
            // Remove from collapsed set to expand
            if (collapsed.has(stageKey)) {
                collapsed.delete(stageKey);
                this.collapsedStages.set(new Set(collapsed));
            }
            // Scroll to the stage element
            setTimeout(() => {
                const stageEl = this.el.nativeElement.querySelector(`[data-stage-id="${stageId}"]`);
                stageEl?.scrollIntoView({ behavior: "smooth", block: "start" });
            });
        }
    }
}
